import { useMemo, useState, type ReactNode } from 'react';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  saveOnsiteLiveBranchSet,
  saveOnsiteLiveSecurityGroup,
  saveOnsiteLiveSubscriptionGroup,
} from '@/api/onsiteLiveAdmin';
import type {
  OnsiteLiveBranchSet,
  OnsiteLiveLookups,
  OnsiteLiveSecurityGroup,
  OnsiteLiveSubscriptionGroup,
} from '@/types/oscar';
import {
  branchSetSchema,
  branchSetToForm,
  securityGroupSchema,
  securityGroupToForm,
  splitBranchCodes,
  subscriptionGroupSchema,
  subscriptionGroupToForm,
  toBranchSetPayload,
  toSecurityGroupPayload,
  toSubscriptionGroupPayload,
  type BranchSetFormData,
  type SecurityGroupFormData,
  type SubscriptionGroupFormData,
} from './schema';
import { ErrorBanner, FieldError } from '../BranchRelationships/shared';
import { btnPrimary, btnSecondary, inputCls } from '../BranchRelationships/styles';

/**
 * The three reference lists behind the user screen — OnBudget's Security
 * Groups, Subscription Group and Subscription Branch screens. Each is a table
 * plus one editor panel. There is no delete in OnBudget either: a group is
 * switched off (Active / Inactive), so the legacy ids on SQ04 never vanish.
 */

type Editing<T> = { row: T | null } | null;

function useSaved() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['onsitelive-admin'] });
}

// ── Security groups ───────────────────────────────────────────────────────────

export function SecurityGroupsTab({ lookups, canWrite }: { lookups: OnsiteLiveLookups | undefined; canWrite: boolean }) {
  const [editing, setEditing] = useState<Editing<OnsiteLiveSecurityGroup>>(null);
  const [search, setSearch] = useState('');
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (lookups?.SecurityGroups ?? []).filter((g) => !term || `${g.Name} ${g.Description ?? ''}`.toLowerCase().includes(term));
  }, [lookups, search]);

  return (
    <ListShell
      intro="Which OnsiteLive reports a person can open. A user gets a report through any group they are in."
      search={search}
      onSearch={setSearch}
      addLabel="New security group"
      canWrite={canWrite}
      onAdd={() => setEditing({ row: null })}
      editor={editing && (
        <SecurityGroupEditor key={editing.row?.ReportSecurityGroupId ?? 'new'} row={editing.row} onDone={() => setEditing(null)} />
      )}
      head={['Id', 'Name', 'Description', 'Active users', 'Status']}
      canEdit={canWrite}
      empty="No security groups match."
      rows={rows.map((g) => ({
        key: g.ReportSecurityGroupId,
        cells: [g.ReportSecurityGroupId, g.Name, g.Description ?? '', g.ActiveMembers, <Status key="s" on={g.Active} />],
        onEdit: () => setEditing({ row: g }),
        editLabel: `Edit ${g.Name}`,
      }))}
    />
  );
}

function SecurityGroupEditor({ row, onDone }: { row: OnsiteLiveSecurityGroup | null; onDone: () => void }) {
  const saved = useSaved();
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<SecurityGroupFormData>({
    resolver: zodResolver(securityGroupSchema),
    defaultValues: securityGroupToForm(row),
  });
  const save = useMutation({
    mutationFn: (d: SecurityGroupFormData) => saveOnsiteLiveSecurityGroup(row?.ReportSecurityGroupId ?? null, toSecurityGroupPayload(d)),
    onSuccess: () => { void saved(); onDone(); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save the group.'),
  });
  const err = formState.errors;
  return (
    <EditorShell
      title={row ? `Security group ${row.ReportSecurityGroupId}` : 'New security group'}
      error={error}
      busy={save.isPending}
      onCancel={onDone}
      onSubmit={() => void handleSubmit((d) => save.mutate(d))()}
    >
      <TextField label="Name" error={err.Name?.message} reg={register('Name')} />
      <TextField label="Description" error={err.Description?.message} reg={register('Description')} />
      <label className="inline-flex items-center gap-2 text-sm font-semibold text-darkGrey">
        <input type="checkbox" {...register('Active')} /> Active
      </label>
    </EditorShell>
  );
}

// ── Subscription groups ───────────────────────────────────────────────────────

export function SubscriptionGroupsTab({ lookups, canWrite }: { lookups: OnsiteLiveLookups | undefined; canWrite: boolean }) {
  const [editing, setEditing] = useState<Editing<OnsiteLiveSubscriptionGroup>>(null);
  const [search, setSearch] = useState('');
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (lookups?.SubscriptionGroups ?? []).filter((g) => !term || `${g.Name ?? ''} ${g.Comments ?? ''}`.toLowerCase().includes(term));
  }, [lookups, search]);

  return (
    <ListShell
      intro="Groups used to send scheduled report subscriptions to a set of people."
      search={search}
      onSearch={setSearch}
      addLabel="New subscription group"
      canWrite={canWrite}
      onAdd={() => setEditing({ row: null })}
      editor={editing && (
        <SubscriptionGroupEditor key={editing.row?.SubGpId ?? 'new'} row={editing.row} onDone={() => setEditing(null)} />
      )}
      head={['Id', 'Name', 'Comments', 'Members', 'Status']}
      canEdit={canWrite}
      empty="No subscription groups match."
      rows={rows.map((g) => ({
        key: g.SubGpId,
        cells: [g.SubGpId, g.Name ?? '', g.Comments ?? '', g.Members, <Status key="s" on={!g.Inactive} />],
        onEdit: () => setEditing({ row: g }),
        editLabel: `Edit ${g.Name ?? g.SubGpId}`,
      }))}
    />
  );
}

function SubscriptionGroupEditor({ row, onDone }: { row: OnsiteLiveSubscriptionGroup | null; onDone: () => void }) {
  const saved = useSaved();
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<SubscriptionGroupFormData>({
    resolver: zodResolver(subscriptionGroupSchema),
    defaultValues: subscriptionGroupToForm(row),
  });
  const save = useMutation({
    mutationFn: (d: SubscriptionGroupFormData) => saveOnsiteLiveSubscriptionGroup(row?.SubGpId ?? null, toSubscriptionGroupPayload(d)),
    onSuccess: () => { void saved(); onDone(); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save the group.'),
  });
  const err = formState.errors;
  return (
    <EditorShell
      title={row ? `Subscription group ${row.SubGpId}` : 'New subscription group'}
      error={error}
      busy={save.isPending}
      onCancel={onDone}
      onSubmit={() => void handleSubmit((d) => save.mutate(d))()}
    >
      <TextField label="Name" error={err.Name?.message} reg={register('Name')} />
      <TextField label="Comments" error={err.Comments?.message} reg={register('Comments')} />
      <label className="inline-flex items-center gap-2 text-sm font-semibold text-darkGrey">
        <input type="checkbox" {...register('Inactive')} /> Inactive
      </label>
    </EditorShell>
  );
}

// ── Branch sets (SubscriptionBranches) ────────────────────────────────────────

export function BranchSetsTab({ lookups, canWrite }: { lookups: OnsiteLiveLookups | undefined; canWrite: boolean }) {
  const [editing, setEditing] = useState<Editing<OnsiteLiveBranchSet>>(null);
  const [search, setSearch] = useState('');
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (lookups?.BranchSets ?? []).filter((b) => !term || `${b.Description ?? ''} ${b.BranchCodes ?? ''}`.toLowerCase().includes(term));
  }, [lookups, search]);

  return (
    <ListShell
      intro="Named sets of branches. A user's subscription branch decides which branches their reports cover; it also feeds Baseplan PO branch rights through the SQ06 limits job."
      search={search}
      onSearch={setSearch}
      addLabel="New branch set"
      canWrite={canWrite}
      onAdd={() => setEditing({ row: null })}
      editor={editing && <BranchSetEditor key={editing.row?.SubBranchid ?? 'new'} row={editing.row} onDone={() => setEditing(null)} />}
      head={['Id', 'Description', 'Branches', 'Level']}
      canEdit={canWrite}
      empty="No branch sets match."
      rows={rows.map((b) => {
        const codes = splitBranchCodes(b.BranchCodes ?? '');
        return {
          key: b.SubBranchid,
          cells: [
            b.SubBranchid,
            b.Description ?? '',
            <span key="c" className="font-mono text-xs" title={codes.join(', ')}>
              {codes.length > 8 ? `${codes.slice(0, 8).join(', ')} … (+${codes.length - 8})` : codes.join(', ')}
            </span>,
            [b.Company && 'Company', b.Region && 'Region', b.Area && 'Area'].filter(Boolean).join(', ') || 'Branch',
          ],
          onEdit: () => setEditing({ row: b }),
          editLabel: `Edit ${b.Description ?? b.SubBranchid}`,
        };
      })}
    />
  );
}

function BranchSetEditor({ row, onDone }: { row: OnsiteLiveBranchSet | null; onDone: () => void }) {
  const saved = useSaved();
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<BranchSetFormData>({
    resolver: zodResolver(branchSetSchema),
    defaultValues: branchSetToForm(row),
  });
  const save = useMutation({
    mutationFn: (d: BranchSetFormData) => saveOnsiteLiveBranchSet(row?.SubBranchid ?? null, toBranchSetPayload(d)),
    onSuccess: () => { void saved(); onDone(); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save the branch set.'),
  });
  const err = formState.errors;
  return (
    <EditorShell
      title={row ? `Branch set ${row.SubBranchid}` : 'New branch set'}
      error={error}
      busy={save.isPending}
      onCancel={onDone}
      onSubmit={() => void handleSubmit((d) => save.mutate(d))()}
    >
      <TextField label="Description" error={err.Description?.message} reg={register('Description')} />
      <label className="flex flex-col text-xs font-semibold text-darkGrey md:col-span-2">
        Branch codes <span className="font-normal text-darkGrey/60">(comma, space or one per line)</span>
        <textarea rows={3} {...register('BranchCodes')} className={`${inputCls} mt-1 font-mono font-normal`} />
        {err.BranchCodes && <FieldError message={err.BranchCodes.message} />}
      </label>
      <div className="flex gap-4 text-sm font-semibold text-darkGrey">
        <label className="inline-flex items-center gap-1.5"><input type="checkbox" {...register('Company')} /> Company</label>
        <label className="inline-flex items-center gap-1.5"><input type="checkbox" {...register('Region')} /> Region</label>
        <label className="inline-flex items-center gap-1.5"><input type="checkbox" {...register('Area')} /> Area</label>
      </div>
    </EditorShell>
  );
}

// ── Shared shells ─────────────────────────────────────────────────────────────

function Status({ on }: { on: boolean }) {
  return on
    ? <span className="rounded-full bg-green/15 px-2 py-0.5 text-xs font-semibold text-green">Active</span>
    : <span className="rounded-full bg-darkGrey/10 px-2 py-0.5 text-xs font-semibold text-darkGrey/70">Inactive</span>;
}

function ListShell({
  intro, search, onSearch, addLabel, canWrite, onAdd, editor, head, rows, canEdit, empty,
}: {
  intro: string;
  search: string;
  onSearch: (v: string) => void;
  addLabel: string;
  canWrite: boolean;
  onAdd: () => void;
  editor: ReactNode;
  head: string[];
  rows: { key: number; cells: ReactNode[]; onEdit: () => void; editLabel: string }[];
  canEdit: boolean;
  empty: string;
}) {
  return (
    <div>
      <p className="text-sm text-darkGrey">{intro}</p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search" aria-label="Search" className={`${inputCls} w-64`} />
        {canWrite && <button type="button" className={btnSecondary} onClick={onAdd}>{addLabel}</button>}
      </div>
      {editor}
      <div className="mt-4 overflow-x-auto rounded border border-border bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-navy text-left text-white">
            <tr>
              {head.map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}
              {canEdit && <th className="px-3 py-2"><span className="sr-only">Actions</span></th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={head.length + 1} className="px-3 py-4 text-darkGrey/70">{empty}</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={r.key} className={i % 2 ? 'bg-[#F4F6F9]' : 'bg-white'}>
                {r.cells.map((c, j) => <td key={j} className={`px-3 py-2 ${j === 0 ? 'font-mono text-xs text-darkGrey/70' : ''}`}>{c}</td>)}
                {canEdit && (
                  <td className="px-3 py-2 text-right">
                    <button type="button" onClick={r.onEdit} aria-label={r.editLabel} className="text-xs font-semibold text-navy hover:underline">
                      Edit
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditorShell({
  title, error, busy, onCancel, onSubmit, children,
}: {
  title: string;
  error: string | null;
  busy: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  children: ReactNode;
}) {
  return (
    <form
      aria-label={title}
      className="mt-3 rounded border border-navy/30 bg-[#F4F6F9] p-4"
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
    >
      <h2 className="font-heading text-base font-bold text-navy">{title}</h2>
      <fieldset disabled={busy} className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">{children}</fieldset>
      {error && <ErrorBanner message={error} />}
      <div className="mt-3 flex gap-2">
        <button type="submit" className={btnPrimary} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className={btnSecondary} onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}

function TextField({ label, error, reg }: { label: string; error?: string; reg: UseFormRegisterReturn }) {
  return (
    <label className="flex flex-col text-xs font-semibold text-darkGrey">
      {label}
      <input {...reg} className={`${inputCls} mt-1 font-normal`} />
      {error && <FieldError message={error} />}
    </label>
  );
}
